import React, { useMemo, useState } from 'react';
import { Dialog, Box, Typography, IconButton, ToggleButton, ToggleButtonGroup, Chip } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import timesheetService from '../../../services/timesheetService';
import { buildWeekView } from '../weekView';
import { attendShort } from '../../../services/attendCodes';

// Week View — вся неделя на одном экране, крупно, под айпад: по работам
// (работа → люди × дни) или по людям (человек → работы × дни), с итогами.
// Только просмотр: правят часы по-прежнему в сетке.

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayHead = d => { const dt = new Date(d + 'T00:00:00'); return { wd: WD[dt.getDay()], md: `${dt.getMonth() + 1}/${dt.getDate()}` }; };
const fmt = v => (v > 0 ? String(Math.round(v * 100) / 100) : '');
const todayIso = () => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`; };

const cell = { textAlign: 'center', py: 1, px: 0.75, fontSize: '0.95rem', borderBottom: '1px solid #e2e8f0', minWidth: 52 };
const nameCell = { textAlign: 'left', py: 1, px: 1.25, fontSize: '0.95rem', borderBottom: '1px solid #e2e8f0',
    position: 'sticky', left: 0, zIndex: 1, minWidth: 190, maxWidth: 260 };

function GridTable({ rows, weekDates, today, firstColLabel }) {
    const dayTotals = Object.fromEntries(weekDates.map(d => [d, rows.reduce((s, r) => s + (r.days[d] || 0), 0)]));
    const total = rows.reduce((s, r) => s + r.total, 0);
    return (
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <Box component="th" sx={{ ...nameCell, bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.8rem', color: '#475569', textTransform: 'uppercase' }}>{firstColLabel}</Box>
                        {weekDates.map(d => {
                            const h = dayHead(d);
                            return (
                                <Box component="th" key={d} sx={{ ...cell, bgcolor: d === today ? '#dbeafe' : '#f8fafc', fontWeight: 700 }}>
                                    <Box sx={{ fontSize: '0.8rem' }}>{h.wd}</Box>
                                    <Box sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>{h.md}</Box>
                                </Box>
                            );
                        })}
                        <Box component="th" sx={{ ...cell, bgcolor: '#f8fafc', fontWeight: 800, color: '#15803d', minWidth: 64 }}>Total</Box>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.id}>
                            <Box component="td" sx={{ ...nameCell, bgcolor: i % 2 ? '#f8fafc' : '#fff' }}>
                                <Box sx={{ fontWeight: 600, lineHeight: 1.25 }}>{r.label}</Box>
                                {r.sub && <Box sx={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.2 }}>{r.sub}</Box>}
                            </Box>
                            {weekDates.map(d => (
                                <Box component="td" key={d} sx={{ ...cell, bgcolor: d === today ? '#eff6ff' : (i % 2 ? '#f8fafc' : '#fff'),
                                    color: r.days[d] > 0 ? '#0f172a' : '#cbd5e1' }}>
                                    {fmt(r.days[d]) || '·'}
                                    {r.codes && r.codes[d] && (
                                        <Box sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#b45309' }}>{attendShort(r.codes[d])}</Box>
                                    )}
                                </Box>
                            ))}
                            <Box component="td" sx={{ ...cell, fontWeight: 800, color: '#15803d', bgcolor: i % 2 ? '#f8fafc' : '#fff' }}>{fmt(r.total) || '0'}</Box>
                        </tr>
                    ))}
                    <tr>
                        <Box component="td" sx={{ ...nameCell, bgcolor: '#fff', fontWeight: 800, borderTop: '2px solid #94a3b8' }}>Total</Box>
                        {weekDates.map(d => (
                            <Box component="td" key={d} sx={{ ...cell, fontWeight: 800, borderTop: '2px solid #94a3b8',
                                bgcolor: d === today ? '#eff6ff' : '#fff', color: dayTotals[d] > 0 ? '#b45309' : '#cbd5e1' }}>{fmt(dayTotals[d]) || '·'}</Box>
                        ))}
                        <Box component="td" sx={{ ...cell, fontWeight: 900, color: '#15803d', borderTop: '2px solid #94a3b8' }}>{fmt(total) || '0'}</Box>
                    </tr>
                </tbody>
            </Box>
        </Box>
    );
}

function Section({ title, chips, children }) {
    return (
        <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, mb: 2, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 1.5, py: 1.25, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                {title}
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75 }}>{chips}</Box>
            </Box>
            {children}
        </Box>
    );
}

export default function WeekViewModal({ open, onClose, jobs, employees, weekRecords, currentDate }) {
    const [mode, setMode] = useState('job');
    const weekDates = useMemo(() => timesheetService.getWeekDates(currentDate), [currentDate]);
    const today = todayIso();
    const v = useMemo(() => (open ? buildWeekView({
        jobs, employees, weekRecords, weekDates,
        jobLabel: j => ({ code: timesheetService.getJobCode(j), name: timesheetService.getJobName(j), customer: timesheetService.getJobCustomer(j) }),
        employeeName: e => timesheetService.getEmployeeName(e),
    }) : null), [open, jobs, employees, weekRecords, weekDates]);
    if (!open || !v) return null;

    const first = dayHead(weekDates[0]); const last = dayHead(weekDates[weekDates.length - 1]);
    const hoursChip = h => <Chip label={`${fmt(h) || 0} h`} size="small" sx={{ fontWeight: 800, bgcolor: '#dcfce7', color: '#166534' }} />;

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', px: 2, py: 1.25,
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', lineHeight: 1.2 }}>Week view</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>{first.wd} {first.md} – {last.wd} {last.md}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {hoursChip(v.total)}
                    <Chip label={`${v.jobs.length} ${v.jobs.length === 1 ? 'job' : 'jobs'}`} size="small" sx={{ fontWeight: 700 }} />
                    <Chip label={`${v.employees.length} ${v.employees.length === 1 ? 'person' : 'people'}`} size="small" sx={{ fontWeight: 700 }} />
                </Box>
                <ToggleButtonGroup value={mode} exclusive size="small" onChange={(e, m) => m && setMode(m)} sx={{ ml: 'auto' }}>
                    <ToggleButton value="job" sx={{ px: 2, fontWeight: 700, textTransform: 'none' }}>By job</ToggleButton>
                    <ToggleButton value="employee" sx={{ px: 2, fontWeight: 700, textTransform: 'none' }}>By employee</ToggleButton>
                </ToggleButtonGroup>
                <IconButton onClick={onClose} aria-label="Close week view" sx={{ width: 48, height: 48, bgcolor: '#f1f5f9' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 2, bgcolor: '#f8fafc', flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {v.total === 0 && v.jobs.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', color: '#64748b', py: 6, fontSize: '1rem' }}>No hours this week yet.</Typography>
                ) : mode === 'job' ? (
                    v.jobs.map(j => (
                        <Section key={j.id}
                            title={<>
                                <Chip label={`#${j.code}`} color="primary" size="small" sx={{ fontWeight: 800 }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{j.name}</Typography>
                                {j.customer && <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>{j.customer}</Typography>}
                            </>}
                            chips={<>{hoursChip(j.total)}<Chip label={`${j.people.length} ${j.people.length === 1 ? 'person' : 'people'}`} size="small" /></>}>
                            <GridTable weekDates={weekDates} today={today} firstColLabel="Employee"
                                rows={j.people.map(p => ({ id: p.id, label: p.name, days: p.days, codes: p.codes, total: p.total }))} />
                        </Section>
                    ))
                ) : (
                    v.employees.map(e => (
                        <Section key={e.id}
                            title={<Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>{e.name}</Typography>}
                            chips={<>{hoursChip(e.total)}<Chip label={`${e.jobs.length} ${e.jobs.length === 1 ? 'job' : 'jobs'}`} size="small" /></>}>
                            <GridTable weekDates={weekDates} today={today} firstColLabel="Job"
                                rows={e.jobs.map(j => ({ id: j.id, label: `#${j.code} ${j.name}`, sub: j.customer, days: j.days, codes: j.codes, total: j.total }))} />
                        </Section>
                    ))
                )}
            </Box>
        </Dialog>
    );
}
